import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";

/**
 * Bauformen — und die Namensfrage.
 *
 * Zwei Beobachtungen aus der Search Console: "Bauform / Typ" ist mit rund
 * 18.000 Impressionen das drittgrößte Themenfeld, und die Domain rankt für
 * "photobooth" (16.848 Impressionen) sowie "fotoautomat" (15.308) auf
 * Position 16 beziehungsweise 13 — bei zusammen 21 Klicks. Beide Wörter
 * kommen auf der Seite bislang praktisch nicht vor.
 *
 * Der Abschnitt löst beides auf einmal: Er erklärt die Bauformen, die man
 * wirklich unterscheiden muss, und sagt nebenbei, dass Fotobox, Photobooth
 * und Fotoautomat dieselbe Sache meinen. Das ist keine Keyword-Kosmetik —
 * es ist genau die Frage, die jemand hat, der zum ersten Mal sucht.
 */

const BAUFORMEN = [
  {
    schluessel: "Standard",
    titel: "Klassische Fotobox",
    text: "Kamera, Licht und Bildschirm auf einem Stativ oder einer Säule. Die verbreitetste Bauform, passt in jede Ecke und braucht nur eine Steckdose.",
  },
  {
    schluessel: "Spiegel",
    titel: "Spiegel-Fotobox (Magic Mirror)",
    href: "/fotobox-spiegel-mieten",
    text: "Ein mannshoher Spiegel mit Kamera dahinter. Gäste sehen sich in Lebensgröße, die Bedienung läuft über den Spiegel selbst. Auffälliger, aber schwerer und teurer.",
  },
  {
    schluessel: "Kabine",
    titel: "Fotokabine",
    text: "Die geschlossene Variante mit Vorhang — wie am Bahnhof, nur schöner. Braucht spürbar mehr Platz, dafür sind die Bilder blickgeschützt.",
  },
  {
    schluessel: "Fahrzeug",
    titel: "Fotobus",
    text: "Ein umgebauter Bulli oder Anhänger, der als Fotostation dient. Ein Blickfang für draußen — mit entsprechendem Preis und Platzbedarf.",
  },
];

export default function BauformenSektion({ bauformen = {} }) {
  const vorhanden = BAUFORMEN.filter((b) => bauformen[b.schluessel]?.boxen);

  return (
    <SectionHeader
      id="bauformen"
      title="Welche Bauform passt zu eurer Feier?"
      subtitle="Vier Bauformen, ein Prinzip — und drei Namen für dieselbe Sache."
    >
      <div className="mx-auto mt-8 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        <div className="grid gap-4 sm:grid-cols-2">
          {vorhanden.map((b) => {
            const daten = bauformen[b.schluessel];
            return (
              <div
                key={b.schluessel}
                className="rounded-2xl bg-slate-50 p-5 ring-1 ring-slate-200/70"
              >
                <h3 className="text-base font-semibold text-slate-900">
                  {b.href ? (
                    <Link
                      href={b.href}
                      className="underline-offset-2 hover:text-[#FF5744] hover:underline"
                    >
                      {b.titel}
                    </Link>
                  ) : (
                    b.titel
                  )}
                </h3>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {daten.boxen} {daten.boxen === 1 ? "Angebot" : "Angebote"}
                  {daten.min ? ` · ab ${daten.min} €` : ""}
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {b.text}
                </p>
              </div>
            );
          })}
        </div>

        <h3 className="mt-10 text-xl font-semibold text-slate-900">
          Fotobox, Photobooth oder Fotoautomat — was ist der Unterschied?
        </h3>
        <p className="mt-3">
          Keiner. Die Begriffe meinen dieselbe Sache, sie stammen nur aus
          verschiedenen Ecken:{" "}
          <strong className="font-semibold text-slate-900">Photobooth</strong>{" "}
          ist das englische Original,{" "}
          <strong className="font-semibold text-slate-900">Fotobox</strong> die
          im deutschsprachigen Raum übliche Bezeichnung.{" "}
          <strong className="font-semibold text-slate-900">Fotoautomat</strong>{" "}
          erinnert an die Passbildkabine und wird häufig für die geschlossene
          Bauform benutzt. Wer{" "}
          <strong className="font-semibold text-slate-900">Fotokabine</strong>,{" "}
          <strong className="font-semibold text-slate-900">Fotokiste</strong>{" "}
          oder <strong className="font-semibold text-slate-900">Selfiebox</strong>{" "}
          sucht, meint ebenfalls dasselbe Gerät. Im Vergleich findest du alle
          unter einem Dach — die Unterschiede, auf die es wirklich ankommt,
          liegen bei Kamera, Licht und Druck.
        </p>
        <p className="mt-4">
          <Link
            href="/fotobox-vergleich"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Alle Bauformen im Vergleich filtern
          </Link>
        </p>
      </div>
    </SectionHeader>
  );
}
